/**
 * PlayerGuard — Intercepta estados de infraestrutura antes de renderizar
 * o player visual.
 *
 * Cascata de estados verificados (em ordem de prioridade):
 *   loading   → spinner de "Conectando ao player…"
 *   revogado  → tela de "Licença Revogada"
 *   error     → tela de "Tela não encontrada"
 *   (limpo)   → renderiza children com estado garantidamente válido
 *
 * O componente Player interno nunca precisa checar null/loading — recebe
 * apenas estado limpo e pronto para renderizar os modos de exibição finais.
 */

import type { ReactNode } from 'react';

// ── Telas de infraestrutura ────────────────────────────────────────────────────

function TelaCarregando() {
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <div className="relative w-14 h-14 mx-auto mb-5">
          <div className="absolute inset-0 rounded-full border-2 border-orange-500/20" />
          <div className="absolute inset-0 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
          <div className="absolute inset-[5px] rounded-full border border-orange-500/10 animate-pulse" />
        </div>
        <p className="text-slate-300 text-sm font-medium">Conectando ao player</p>
        <p className="text-slate-600 text-xs mt-1">Aguarde...</p>
      </div>
    </div>
  );
}

function TelaRevogada({ telaId }: { telaId?: string }) {
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-center max-w-sm px-6 animate-scale-in">
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-5 shadow-[0_0_32px_rgba(239,68,68,0.15)]">
          <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>
        <p className="text-white font-semibold text-lg">Licença Revogada</p>
        <p className="text-slate-500 text-sm mt-2">Esta tela foi desativada pelo administrador.</p>
        {telaId && (
          <p className="text-slate-700 text-xs mt-4 font-mono">{telaId}</p>
        )}
      </div>
    </div>
  );
}

function TelaErro({ error, telaId }: { error: string; telaId?: string }) {
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-center max-w-sm px-4 animate-scale-in">
        <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-5 shadow-[0_0_32px_rgba(245,158,11,0.1)]">
          <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-white text-lg font-medium">Tela não encontrada</p>
        {error && <p className="text-slate-400 text-sm mt-2">{error}</p>}
        {telaId && (
          <p className="text-slate-600 text-xs mt-2 font-mono break-all">ID: {telaId}</p>
        )}
      </div>
    </div>
  );
}

// ── Guard ──────────────────────────────────────────────────────────────────────

interface PlayerGuardProps {
  telaId:   string | undefined;
  loading:  boolean;
  error:    string;
  revogado: boolean;
  children: ReactNode;
}

export default function PlayerGuard({
  telaId,
  loading,
  error,
  revogado,
  children,
}: PlayerGuardProps) {
  if (loading)          return <TelaCarregando />;
  if (revogado)         return <TelaRevogada telaId={telaId} />;
  if (error || !telaId) return <TelaErro error={error} telaId={telaId} />;
  return <>{children}</>;
}
